import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { generateLinkedInPost, LinkedInTone } from '../../services/linkedInPost';
import { getConversations, Conversation } from '../../services/conversations';

const TONES: LinkedInTone[] = ['thought-leadership', 'personal-story', 'lesson-learned', 'achievement'];

export function LinkedInPostsPage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { t, language } = useLanguage();

  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState<LinkedInTone>('thought-leadership');
  const [useCareerContext, setUseCareerContext] = useState(true);
  const [generatedPost, setGeneratedPost] = useState('');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [recentTopics, setRecentTopics] = useState<string[]>([]);

  // Load recent conversation summaries for topic suggestions
  useEffect(() => {
    (async () => {
      try {
        const convos = await getConversations();
        const topics = convos
          .filter((c: Conversation) => c.summary)
          .slice(0, 5)
          .map((c: Conversation) => {
            // Extract first meaningful line from summary
            const lines = (c.summary || '').split('\n').filter(Boolean);
            const topicLine = lines.find(l => !l.startsWith('#') && !l.startsWith('-') && l.length > 10);
            return topicLine?.slice(0, 80) || c.title || c.preview?.slice(0, 80) || '';
          })
          .filter(Boolean);
        setRecentTopics(topics);
      } catch {
        // Silently fail — suggestions are optional
      }
    })();
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!topic.trim()) {
      setError(t('linkedin.emptyTopic'));
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const result = await generateLinkedInPost(topic.trim(), tone, language, useCareerContext);
      setGeneratedPost(result.post);
      setHashtags(result.hashtags);
    } catch (err: any) {
      console.error('LinkedIn post generation failed:', err);
      setError(t('linkedin.error'));
    } finally {
      setGenerating(false);
    }
  }, [topic, tone, language, useCareerContext, t]);

  const handleCopy = useCallback(async () => {
    const hashtagText = hashtags.length > 0 ? '\n\n' + hashtags.map(h => `#${h}`).join(' ') : '';
    await navigator.clipboard.writeText(generatedPost + hashtagText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generatedPost, hashtags]);

  const handleRegenerate = useCallback(() => {
    handleGenerate();
  }, [handleGenerate]);

  const handleNewPost = useCallback(() => {
    setTopic('');
    setGeneratedPost('');
    setHashtags([]);
    setError('');
    setCopied(false);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Failed to sign out', error);
    }
  };

  return (
    <div
      data-testid="linkedin-posts-page"
      style={{ minHeight: '100vh', backgroundColor: 'transparent' }}
    >
      <header
        style={{
          backgroundColor: 'var(--surface)',
          padding: '16px 32px',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: '600' }}>My Job Odyssey</h1>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <LanguageSwitcher />
          <button
            onClick={handleSignOut}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {t('common.logout')}
          </button>
        </div>
      </header>

      <main style={{ padding: '40px 32px', maxWidth: '800px', margin: '0 auto' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            marginBottom: '20px',
            padding: '8px 16px',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          &larr; {t('dashboard.backToDashboard')}
        </button>

        {/* Header */}
        <div
          style={{
            backgroundColor: 'var(--surface)',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)',
            marginBottom: '24px',
          }}
        >
          <h2 style={{ marginTop: 0 }}>{t('linkedin.title')}</h2>
          <p style={{ marginBottom: 0, color: 'var(--text-muted)' }}>{t('linkedin.subtitle')}</p>
        </div>

        {/* Topic Input */}
        <div
          style={{
            backgroundColor: 'var(--surface)',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)',
            marginBottom: '24px',
          }}
        >
          <label
            htmlFor="topic"
            style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}
          >
            {t('linkedin.topicLabel')}
          </label>
          <textarea
            id="topic"
            value={topic}
            onChange={e => setTopic(e.target.value)}
            placeholder={t('linkedin.topicPlaceholder')}
            rows={3}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              fontSize: '14px',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />

          {/* Recent topic suggestions */}
          {recentTopics.length > 0 && !generatedPost && (
            <div style={{ marginTop: '12px' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-muted)', marginRight: '8px' }}>
                {t('linkedin.recentTopics')}:
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                {recentTopics.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setTopic(suggestion)}
                    style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      backgroundColor: 'var(--surface-hover, #f0f0f0)',
                      border: '1px solid var(--border)',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      color: 'var(--text)',
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tone Picker */}
        <div
          style={{
            backgroundColor: 'var(--surface)',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)',
            marginBottom: '24px',
          }}
        >
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '12px' }}>
            {t('linkedin.toneLabel')}
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {TONES.map(t_key => (
              <button
                key={t_key}
                onClick={() => setTone(t_key)}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: tone === t_key ? '2px solid var(--primary, #2563eb)' : '1px solid var(--border)',
                  backgroundColor: tone === t_key ? 'var(--primary-bg, #eff6ff)' : 'var(--surface)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                  {t(`linkedin.tones.${t_key}`)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {t(`linkedin.toneDescriptions.${t_key}`)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Career Context Toggle */}
        <div
          style={{
            backgroundColor: 'var(--surface)',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <input
            type="checkbox"
            id="careerContext"
            checked={useCareerContext}
            onChange={e => setUseCareerContext(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <div>
            <label htmlFor="careerContext" style={{ fontWeight: 600, cursor: 'pointer', fontSize: '14px' }}>
              {t('linkedin.useCareerContext')}
            </label>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
              {t('linkedin.useCareerContextHint')}
            </p>
          </div>
        </div>

        {/* Generate Button */}
        {!generatedPost && (
          <button
            onClick={handleGenerate}
            disabled={generating || !topic.trim()}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: 600,
              backgroundColor: generating || !topic.trim() ? 'var(--border)' : 'var(--primary, #2563eb)',
              color: generating || !topic.trim() ? 'var(--text-muted)' : '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: generating || !topic.trim() ? 'not-allowed' : 'pointer',
              marginBottom: '24px',
            }}
          >
            {generating ? t('linkedin.generating') : t('linkedin.generate')}
          </button>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        {/* Generated Output */}
        {generatedPost && (
          <div
            style={{
              backgroundColor: 'var(--surface)',
              padding: '24px',
              borderRadius: '8px',
              boxShadow: 'var(--shadow-sm)',
              marginBottom: '24px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>{t('linkedin.generatedPost')}</h3>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('linkedin.editHint')}</span>
            </div>

            <textarea
              aria-label={t('linkedin.generatedPost')}
              value={generatedPost}
              onChange={e => setGeneratedPost(e.target.value)}
              rows={12}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                fontSize: '14px',
                lineHeight: '1.6',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />

            {/* Hashtags */}
            {hashtags.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', marginRight: '8px' }}>
                  {t('linkedin.hashtags')}:
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                  {hashtags.map((tag, i) => (
                    <span
                      key={i}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        backgroundColor: 'var(--primary-bg, #eff6ff)',
                        color: 'var(--primary, #2563eb)',
                        borderRadius: '12px',
                        fontWeight: 500,
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={handleCopy}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: copied ? '#16a34a' : 'var(--primary, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {copied ? t('linkedin.copied') : t('linkedin.copy')}
              </button>
              <button
                onClick={handleRegenerate}
                disabled={generating}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: 'var(--surface)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  cursor: generating ? 'not-allowed' : 'pointer',
                }}
              >
                {generating ? t('linkedin.generating') : t('linkedin.regenerate')}
              </button>
              <button
                onClick={handleNewPost}
                style={{
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: 'var(--surface)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                {t('linkedin.newPost')}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
